import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useLocation, useNavigate } from "react-router";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { ReajusteCadastroForm, ReajusteCadastroSchema } from "../schemas/FinanceiroReajuste_cadastro/FinanceiroReajuste_cadastro";
import { IndiceReajusteCadastroForm, IndiceReajusteCadastroSchema } from "../schemas/FinanceiroReajuste_cadastro/FinanceiroIndice_cadastro";
import { Reajuste } from "../../domain/models/reajuste";
import { useReajusteListaPagination } from "../hooks/indice_reajuste_cadastro/pagination";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { DropDown } from "../../../../common/domain/models/dropdown";

type LoadReajusteCadastroState = {
    idIndiceReajuste?: number;
    page?: number;
    rowsPerPage?: number;

}

export const useFinanceiroReajusteCadastroViewModel = () => {

    const {
        financeiro: {
            createReajusteUseCase,
            updateReajusteUseCase,

            getAllReajusteUseCase,
            getReajusteByIdUseCase,
            createIndiceReajusteUseCase,
            updateIndiceReajusteUseCase,
            deleteReajusteUseCase,
            getIndiceReajusteByIdUseCase,


        },
        commons: {
            getAllLogAlteracaoUseCase,
            getAllLogAlteracaoByParentUseCase,
            getContratosFinanceiroDropDownUseCase,
        },

    } = useDependencyInjection();


    const changeHistory = useChangeHistory({
        getAllLogAlteracaoUseCase,
        getAllLogAlteracaoByParentUseCase,
        onSetError: (e) => pushErrorNotification("Erro", [e]),
    });

    const hookFormIndice = useForm<IndiceReajusteCadastroForm>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        resolver: zodResolver(IndiceReajusteCadastroSchema),

    });

    const hookFormReajuste = useForm<ReajusteCadastroForm>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        resolver: zodResolver(ReajusteCadastroSchema),

    });

    const location = useLocation();
    const navigate = useNavigate();

    const { pushErrorNotification, pushInfoNotification, setPageLoading } = usePageLoader();

    const [selectedIdReajuste, setSelectedIdReajuste] = useState<number>();
    const [selectedIdIndiceReajuste, setSelectedIdIndiceReajuste] = useState<number>();

    const [openReajusteCadastroModal, setOpenReajusteCadastroCadastroModal] = useState(false);
    const [reajusteLista, setReajusteLista] = useState<Reajuste[]>([]);

    const [dropDownContratoFinanceiro, setDropDownContratoFinanceiro] = useState<DropDown[]>([]);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const pagination = useReajusteListaPagination();


    //#region CARREGAMENTO.


    const loadReajusteLista = useCallback(async (funcState?: LoadReajusteCadastroState, idIndiceReajusteParam?: number) => {

        try {
            const idIndiceReajuste = selectedIdIndiceReajuste ?? idIndiceReajusteParam
            const filteredPage = funcState?.page ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllReajusteUseCase.getAllReajuste(
                idIndiceReajuste ?? 0,
                filteredPage,
                filteredRowsPerPage
            );

            setReajusteLista(getAllReajusteUseCase.items);

            pagination.setRowsPerPage(getAllReajusteUseCase.pageSize);
            pagination.setTotalItems(getAllReajusteUseCase.totalItems);
        } catch (e: unknown) {
            setReajusteLista([]);
            pushErrorNotification('Error', processError(e, 'Falha ao buscar reajustes!'));
        }
    }, [
        selectedIdIndiceReajuste,
        pagination,
        getAllReajusteUseCase,
        pushErrorNotification
    ]);


    const loadIndiceReajusteById = useCallback(async (idIndiceReajuste: number) => {
        try {
            setPageLoading(true);

            const indiceReajusteData = await getIndiceReajusteByIdUseCase.getIndiceReajusteById(idIndiceReajuste)


            hookFormIndice.setValue('idIndiceReajuste', indiceReajusteData?.idIndiceReajuste ?? 0);
            hookFormIndice.setValue('dataCadastro', indiceReajusteData?.dataCadastro ?? new Date());
            hookFormIndice.setValue('status', indiceReajusteData?.status ?? true);
            hookFormIndice.setValue('descricao', indiceReajusteData?.descricao ?? "");

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar indice de reajuste!'));
        } finally {
            setPageLoading(false);
        }
    }, [
        hookFormIndice,
        getIndiceReajusteByIdUseCase,
        pushErrorNotification,
        setPageLoading
    ]);


    const loadReajusteById = useCallback(async (idReajuste: number) => {
        try {

            setPageLoading(true);
            const reajusteData = await getReajusteByIdUseCase.getReajusteById(idReajuste)

            setSelectedIdReajuste(reajusteData?.idReajuste)
            hookFormReajuste.setValue('idReajuste', reajusteData?.idReajuste ?? 0);
            hookFormReajuste.setValue('idIndiceReajuste', reajusteData?.idIndiceReajuste ?? 0);
            hookFormReajuste.setValue('percentual', reajusteData?.percentual ?? 0);
            hookFormReajuste.setValue('mesIndice', reajusteData?.mesIndice ?? 1);
            hookFormReajuste.setValue('aplicarEntrada', 1);


            if (reajusteData?.dataReajuste) {
                const data = new Date(reajusteData.dataReajuste);

                const mes = data.getMonth() + 1; // getMonth() retorna 0-11
                const ano = data.getFullYear();

                hookFormReajuste.setValue('mesReajuste', mes);
                hookFormReajuste.setValue('anoReajuste', ano);
            }

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar indice de reajuste!'));
        } finally {
            setPageLoading(false);
        }
    }, [
        hookFormReajuste,
        getReajusteByIdUseCase,
        pushErrorNotification,
        setPageLoading
    ]);

    const loadDropDownContratosFinanceiro = useCallback(async () => {
        try {

            if (selectedIdIndiceReajuste) {
                const dropDown = await getContratosFinanceiroDropDownUseCase.getContratosFinanceiroDropDown(selectedIdIndiceReajuste);
                setDropDownContratoFinanceiro(dropDown ?? []);
            } else {
                setDropDownContratoFinanceiro([]);
            }

        } catch (e: unknown) {
            throw pushErrorNotification('Error', processError(e, 'Falha ao buscar contratos para o dropdown!'));
        }
    }, [
        selectedIdIndiceReajuste,
        getContratosFinanceiroDropDownUseCase,
        pushErrorNotification,
    ]);




    //#endregion

    //#region PAGE START

    usePageStartBootstrap(async () => {
        try {

            const idIndiceReajusteParam = selectedIdIndiceReajuste ?? location.state?.idIndiceReajuste as number
            if (idIndiceReajusteParam !== undefined) {

                setSelectedIdIndiceReajuste(idIndiceReajusteParam);
                await loadIndiceReajusteById(idIndiceReajusteParam);
                await loadReajusteLista(undefined, idIndiceReajusteParam);

            } else {
                hookFormIndice.setValue('status', true);
                hookFormIndice.setValue('dataCadastro', new Date());
            }

        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar Indices e reajustes!")[0]);
        }

    });

    //#endregion



    //region NEW INDICE REAJUSTE

    const handleNewindiceReajuste = useCallback(() => {
        navigate(location.pathname, { replace: true, state: {} });

        hookFormIndice.reset({
            idIndiceReajuste: 0,
            descricao: "",
            status: true,
            dataCadastro: new Date()
        });

        hookFormReajuste.reset({
            idReajuste: 0,
            idIndiceReajuste: 0,
            percentual: 0,
            mesIndice: 1,
            mesReajuste: 1,
            anoReajuste: new Date().getFullYear(),
            status: true,
            idsContratosFinanceiroIgnorados: []
        });

        setSelectedIdIndiceReajuste(undefined);
        setSelectedIdReajuste(undefined);
        setReajusteLista([]);

        setOpenReajusteCadastroCadastroModal(false);
        setOpenDeleteModal(false);

    }, [
        location,
        hookFormIndice,
        hookFormReajuste,
        navigate,
    ]);

    //#endregion





    //#region EDIÇÃO
    const handleEditReajusteOpenState = useCallback(async (idReajuste: number | null, openState: boolean,) => {
        setOpenReajusteCadastroCadastroModal(openState);

        if (openState && idReajuste && selectedIdIndiceReajuste) {
            await loadReajusteById(idReajuste)
            setSelectedIdReajuste(idReajuste);
            hookFormReajuste.setValue('idReajuste', idReajuste)
            hookFormReajuste.setValue('idIndiceReajuste', selectedIdIndiceReajuste)
            hookFormReajuste.setValue('aplicarEntrada', 0);

            await loadDropDownContratosFinanceiro();

        } else {
            hookFormReajuste.reset()
            setSelectedIdReajuste(undefined);
        }
    }, [
        selectedIdIndiceReajuste,
        hookFormReajuste,
        loadReajusteById,
        loadDropDownContratosFinanceiro,
    ]);



    //#endregion




    //#region CADASTRO 

    const handleReajusteCadastroOpenState = useCallback(async (openState: boolean) => {
        setOpenReajusteCadastroCadastroModal(openState);
        setSelectedIdReajuste(undefined);
        hookFormReajuste.reset();
        hookFormReajuste.setValue('idReajuste', 0)
        hookFormReajuste.setValue('idIndiceReajuste', selectedIdIndiceReajuste ?? 0)
        hookFormReajuste.setValue('idsContratosFinanceiroIgnorados', [])
        hookFormReajuste.setValue('aplicarEntrada', 0);


        await loadDropDownContratosFinanceiro();

    }, [
        hookFormReajuste,
        selectedIdIndiceReajuste,
        loadDropDownContratosFinanceiro
    ]);


    const handleIndiceReajusteCadastroSubmit = useCallback(async (form: IndiceReajusteCadastroForm) => {
        try {
            setSaving(true);
            if (selectedIdIndiceReajuste) {

                await updateIndiceReajusteUseCase.updateIndiceReajuste({
                    idIndiceReajuste: selectedIdIndiceReajuste,
                    dataCadastro: form.dataCadastro,
                    descricao: form.descricao,
                    status: form.status

                });

                await loadReajusteLista(undefined);
                await loadIndiceReajusteById(selectedIdIndiceReajuste);
                pushInfoNotification("Sucesso", "Indice alterado com sucesso!", { isAutoFinish: true });

            } else {

                const newIdIndice = await createIndiceReajusteUseCase.createIndiceReajuste({
                    idIndiceReajuste: 0,
                    dataCadastro: form.dataCadastro,
                    descricao: form.descricao,
                    status: true
                });
                setSelectedIdIndiceReajuste(newIdIndice)
                await loadIndiceReajusteById(newIdIndice);
                await loadReajusteLista(undefined, newIdIndice)
                pushInfoNotification("Sucesso", "Indice salvo com sucesso!", { isAutoFinish: true });


            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar saldo bancário!"));
        } finally {
            setSaving(false);
        }
    }, [
        selectedIdIndiceReajuste,
        createIndiceReajusteUseCase,
        updateIndiceReajusteUseCase,
        loadIndiceReajusteById,
        loadReajusteLista,
        pushInfoNotification,
        pushErrorNotification,

    ]);


    const handleReajusteCadastroSubmit = useCallback(async (form: ReajusteCadastroForm) => {
        try {
            setSaving(true);
            if (form.idIndiceReajuste && form.idReajuste) {

                const mes = form.mesReajuste
                const ano = form.anoReajuste
                const dataReajuste = new Date(ano, mes - 1, 1);

                await updateReajusteUseCase.updateReajuste({
                    idReajuste: form.idReajuste,
                    idIndiceReajuste: form.idIndiceReajuste,
                    percentual: form.percentual,
                    dataReajuste: dataReajuste,
                    mesIndice: form.mesIndice,
                    aplicarEntrada: form.aplicarEntrada === 1 ? true : false,
                    status: form.status,
                    idsContratosFinanceiroIgnorados: form.idsContratosFinanceiroIgnorados ?? []

                });

                await loadReajusteLista(undefined, form.idIndiceReajuste);
                pushInfoNotification("Sucesso", "reajuste alterado com sucesso!", { isAutoFinish: true });

            } else {
                const mes = form.mesReajuste
                const ano = form.anoReajuste
                const dataReajuste = new Date(ano, mes - 1, 1);

                await createReajusteUseCase.createReajuste({
                    idReajuste: 0,
                    idIndiceReajuste: selectedIdIndiceReajuste ?? 0,
                    percentual: form.percentual,
                    dataReajuste: dataReajuste,
                    mesIndice: form.mesIndice,
                    aplicarEntrada: form.aplicarEntrada === 1 ? true : false,
                    status: form.status,
                    idsContratosFinanceiroIgnorados: form.idsContratosFinanceiroIgnorados ?? []

                });

                await loadReajusteLista(undefined, selectedIdIndiceReajuste)
                pushInfoNotification("Sucesso", "reajuste criado com sucesso!", { isAutoFinish: true });

            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar indice!"));
        } finally {
            setSaving(false);
            setOpenReajusteCadastroCadastroModal(false);
            setSelectedIdReajuste(undefined);
        }
    }, [
        selectedIdIndiceReajuste,
        createReajusteUseCase,
        updateReajusteUseCase,
        loadReajusteLista,
        pushInfoNotification,
        pushErrorNotification,
    ]);



    //#endregion




    //#region EXCLUSÃO
    const handleReajusteDeleteOpenState = useCallback((idReajuste: number | null, openState: boolean) => {
        if (openState && idReajuste) {
            setOpenDeleteModal(openState)
            setSelectedIdReajuste(idReajuste)
        } else {
            setSelectedIdReajuste(undefined)
            setOpenDeleteModal(openState)
        }

    }, []);

    const handleConfirmarDeletarReajuste = useCallback(async () => {
        try {
            if (selectedIdReajuste) {
                await deleteReajusteUseCase.deleteReajuste(selectedIdReajuste);
                pushInfoNotification("Sucesso", "Reajuste deletado com sucesso!");
                await loadReajusteLista();
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao deletar conta a pagar!"));
        } finally {
            setOpenDeleteModal(false);
            setSelectedIdReajuste(undefined);

        }
    }, [
        selectedIdReajuste,
        deleteReajusteUseCase,
        loadReajusteLista,
        pushInfoNotification,
        pushErrorNotification
    ]);


    //#endregion






    //#region PAGINAÇÃO.

    const handlePageBack = useCallback(() => {
        navigate('/FinanceiroReajustes_lista')
    }, [navigate]);

    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            await loadReajusteLista();
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de reajustes!'));
        } finally {
            setLoading(false);
        }
    }, [loadReajusteLista, pushErrorNotification]);


    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadReajusteLista({ page: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadReajusteLista, pushErrorNotification]);


    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadReajusteLista({ rowsPerPage, page: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar reajustes!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadReajusteLista, pushErrorNotification]);

    //#endregion

    //#region HISTORICO

    const handleOpenChangeHistory = useCallback((idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
        if (idTabela && tipoTabela) {
            changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
        } else {
            if (true) {
                changeHistory.handleOpenChangeHistory(selectedIdIndiceReajuste ?? 0, LogTipoTabela.INDICE_REAJUSTE, true);
            }
        }
    }, [
        selectedIdIndiceReajuste,
        changeHistory,
    ]);

    //#endregion


    return {
        loading,
        saving,
        errorMessage,
        pagination,
        openReajusteCadastroModal,
        reajusteLista,
        selectedIdIndiceReajuste,
        hookFormIndice,
        hookFormReajuste,
        openDeleteModal,
        changeHistory,
        selectedIdReajuste,
        dropDownContratoFinanceiro,

        handleOpenChangeHistory,
        handleNewindiceReajuste,
        handleReajusteDeleteOpenState,
        handleConfirmarDeletarReajuste,
        handleReajusteCadastroSubmit,
        handlePageBack,
        handleReajusteCadastroOpenState,
        handleIndiceReajusteCadastroSubmit: hookFormIndice.handleSubmit(handleIndiceReajusteCadastroSubmit),
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
        handleEditReajusteOpenState

    };

}


